<template>
    <EditComponent
        dialog-id="addEducationDialog"
        open-dialog-event="educations:add-mode"
        updated-event="educations:updated"
        :confirm-function="update"
        :track-section-opened="EditionOpenedSection.EDUCATIONS"
        :track-existing-element="false"
    >
        <template #edit-header>{{ t('profile.section.educations.title.v2') }}</template>
        <template #edit-body>
            <VJoyWrapper class="profile-education__list-item" direction="column" align="stretch">
                <VJoyInput
                    v-model="element.description"
                    name="description"
                    :required="true"
                    :invalid="r$.description.$error"
                    :placeholder="t('profile.new.education.description')"
                >
                    {{ t('profile.new.education.description') }}
                </VJoyInput>
                <VJoyFormError v-if="r$.description.$error" data-testid="profile-edition-section-education-add-description-error">
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>

                <VJoyInput
                    v-model="element.title"
                    name="title"
                    :required="true"
                    :invalid="r$.title.$error"
                    :placeholder="t('profile.new.education.title')"
                >
                    {{ t('profile.new.education.title') }}
                </VJoyInput>
                <VJoyFormError v-if="r$.title.$error" data-testid="profile-edition-section-education-add-title-error">
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>

                <div class="profile-education__list-item-graduation">
                    <VJoyInput
                        v-model="element.graduationYear"
                        name="graduationYear"
                        type="number"
                        :required="true"
                        :invalid="r$.graduationYear.$error"
                        :disabled="isCurrent()"
                        :placeholder="t('profile.new.certification.graduation.year')"
                    >
                        {{ t('profile.new.certification.graduation.year') }}
                    </VJoyInput>

                    <VJoyCheckbox v-model:checked="noGraduationYear" class="profile-education__list-item-graduation-checkbox">
                        {{ t('newexperience.currentJob') }}
                    </VJoyCheckbox>
                </div>
                <VJoyFormError v-if="r$.graduationYear.$error" data-testid="profile-edition-section-education-add-graduation-year-error">
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>
                <VJoyTextarea
                    :key="`${element.index}`"
                    v-model="element.content"
                    autogrow
                    :placeholder="t('profile.new.education.content.placeholder')"
                ></VJoyTextarea>
            </VJoyWrapper>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {required, minValue, maxValue, integer} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyWrapper, VJoyTextarea, VJoyCheckbox, VJoyFormError, VJoyInput} from '@maltjoy/core-vue';
    import {EditionOpenedSection} from '~/config/TrackingEvents';
    import {educationMapper} from '~/types/infra/EducationMapper';
    import type {EducationRequest} from '~/types/infra/EducationRequest';
    import EditComponent from '@/components/edit/EditComponent.vue';
    import {eventBus} from '@/config/events';
    import {useProfileStore} from '@/store/profileStore';

    const {t} = useTranslation();
    const {updateEducations} = useProfileStore();
    const {educations} = storeToRefs(useProfileStore());
    const currentYear = new Date().getFullYear();

    const element = ref<EducationRequest>(defaultValue());

    const noGraduationYear = computed(() => {
        return element.value.graduationYear === undefined || +element.value.graduationYear === 0;
    });

    const {r$} = useRegle(element, () => ({
        description: {required},
        title: {required},
        graduationYear: element.value?.current ? {} : {required, integer, minValue: minValue(1900), maxValue: maxValue(currentYear)},
    }));

    function isCurrent() {
        return element.value?.current;
    }

    function defaultValue() {
        return {description: '', title: '', graduationYear: currentYear, content: '', id: 0, current: false};
    }

    async function update() {
        const {valid} = await validateForm(r$);
        if (!valid) {
            return false;
        }
        const currentEducations = educations.value;
        const educationRequests =
            currentEducations?.items.map((education, index) => {
                return educationMapper.toRequest(education, index);
            }) || [];
        element.value.id = educationRequests.length;
        educationRequests.push(element.value);

        updateEducations({educations: educationRequests}).then(() => {
            element.value = defaultValue();
            r$.$reset();
            eventBus.emit('educations:updated');
        });
    }
</script>

<style lang="scss" scoped>
    .draggable {
        cursor: grab;
        margin-left: auto;
    }

    .profile-education__list {
        width: 100%;

        &-title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
        }

        &-item {
            display: flex;
            flex-direction: column;

            &-graduation {
                display: flex;
                align-items: center;

                &-checkbox {
                    margin-top: var(--joy-core-spacing-6);
                    margin-left: auto;
                }
            }
        }

        &-actions {
            display: flex;
            justify-content: flex-end;
            margin-top: var(--joy-core-spacing-5);

            & > joy-button {
                margin-left: var(--joy-core-spacing-2);
            }
        }
    }
</style>
